import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import tesoreriaFlujoFondosService, {
  FlujoFondosDetalle,
  FlujoFondosPeriodo,
  FUENTE_LABELS,
} from '../../../services/tesoreriaFlujoFondosService';
import { formatCurrency } from '../../../utils/formatters';

interface Props {
  open: boolean;
  periodo: FlujoFondosPeriodo | null;
  onClose: () => void;
}

function linkForFuente(fuente: string, nroFormulario?: number | null): string | null {
  switch (fuente) {
    case 'cxc':
      return '/ventas/informes/cuentas-cobrar';
    case 'cxp':
      return '/compras/informes/cuentas-pagar';
    case 'chequesTerceros':
      return '/tesoreria/informes/cheques-terceros-cartera';
    case 'chequesPropios':
      return '/tesoreria/informes/cheques-propios';
    case 'remitos':
      return nroFormulario ? `/ventas/remitos` : '/ventas/remitos';
    case 'pedidos':
      return '/ventas/pedidos';
    case 'ordenesCompra':
      return nroFormulario ? `/compras/ordenes/${nroFormulario}` : '/compras/ordenes';
    case 'recepciones':
      return '/compras/remitos';
    default:
      return null;
  }
}

const FlujoFondosDrilldownDrawer: React.FC<Props> = ({ open, periodo, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [fuente, setFuente] = useState<string | undefined>();
  const [detalle, setDetalle] = useState<FlujoFondosDetalle | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    if (!periodo) return;
    setLoading(true);
    try {
      const data = await tesoreriaFlujoFondosService.getDetalle({
        desde: periodo.desde.slice(0, 10),
        hasta: periodo.hasta.slice(0, 10),
        fuente,
        pageNumber: page + 1,
        pageSize,
        incluirVencidosAntes: periodo.esVencido && periodo.indice === 0,
      });
      setDetalle(data);
    } catch {
      enqueueSnackbar('No se pudo cargar el detalle del período', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [periodo, fuente, page, pageSize, enqueueSnackbar]);

  useEffect(() => {
    if (open && periodo) {
      setFuente(undefined);
      setPage(0);
    }
  }, [open, periodo]);

  useEffect(() => {
    if (open && periodo) void load();
  }, [open, periodo, load]);

  const fmtFecha = (v: string) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('es-AR');
  };

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 480,
          maxHeight: isMobile ? '90vh' : '100%',
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0,
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={600} color="#1976d2">
              Detalle del período
            </Typography>
            {periodo ? (
              <Typography variant="body2" color="text.secondary">
                {periodo.etiqueta} · {fmtFecha(periodo.desde)} – {fmtFecha(periodo.hasta)}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Stack>

        {periodo ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Ingresos ${formatCurrency(periodo.ingresos)}`}
              color="success"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Egresos ${formatCurrency(periodo.egresos)}`}
              color="error"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Saldo ${formatCurrency(periodo.saldoAcumulado)}`}
              color={periodo.saldoAcumulado < 0 ? 'error' : 'primary'}
            />
          </Stack>
        ) : null}

        <Divider />

        <Typography variant="subtitle2" fontWeight={600}>
          Por fuente
        </Typography>
        <List dense disablePadding sx={{ maxHeight: 160, overflow: 'auto' }}>
          <ListItemButton selected={!fuente} onClick={() => { setFuente(undefined); setPage(0); }}>
            <ListItemText primary="Todas" secondary={detalle ? `${detalle.totalCount} movimientos` : undefined} />
          </ListItemButton>
          {(detalle?.resumenPorFuente || periodo?.porFuente || []).map((f) => (
            <ListItemButton
              key={f.fuente}
              selected={fuente === f.fuente}
              onClick={() => { setFuente(f.fuente); setPage(0); }}
            >
              <ListItemText
                primary={FUENTE_LABELS[f.fuente] || f.fuente}
                secondary={`+${formatCurrency(f.ingresos)} / -${formatCurrency(f.egresos)} · ${f.cantidad}`}
              />
            </ListItemButton>
          ))}
        </List>

        <Divider />

        <Typography variant="subtitle2" fontWeight={600}>
          Movimientos
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Detalle</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detalle?.items || []).map((item, idx) => {
                  const href = linkForFuente(item.fuente, item.nroFormulario);
                  return (
                    <TableRow
                      key={`${item.fuente}-${item.referencia || idx}-${item.fechaProyectada}`}
                      hover={!!href}
                      sx={{ cursor: href ? 'pointer' : 'default' }}
                      onClick={() => href && navigate(href)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtFecha(item.fechaProyectada)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.comprobante || FUENTE_LABELS[item.fuente] || item.fuente}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.tercero || '—'} · {item.certeza}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: item.sentido === 'I' ? '#2e7d32' : '#c62828', fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        {item.sentido === 'I' ? '+' : '-'}
                        {formatCurrency(item.importe)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!detalle?.items?.length ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Sin movimientos en este período
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
            {detalle ? (
              <TablePagination
                component="div"
                count={detalle.totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Filas"
              />
            ) : null}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default FlujoFondosDrilldownDrawer;
